import {Component, EventEmitter, Input, OnDestroy, Output, ViewEncapsulation,} from '@angular/core';
import {ModelAttributes} from '@app/core/interface/common';
import SignaturePad from 'signature_pad';
import {BehaviorSubject, Subscription} from 'rxjs';
import {delay, filter} from 'rxjs/operators';

enum SIGNATURE_TAB {
  draw,
  type,
  biometrics
}

@Component({
  selector: 'app-signature',
  templateUrl: './signature.component.html',
  styleUrls: ['./signature.component.sass'],
  encapsulation: ViewEncapsulation.Emulated
})
export class SignatureComponent implements OnDestroy {

  @Input() public signature: ModelAttributes;

  @Output() public changeSignEvent = new EventEmitter<ModelAttributes>();

  public isModalVisible$ = new BehaviorSubject(false);

  public activeTab$ = new BehaviorSubject(SIGNATURE_TAB.draw);

  public get isSignExist(): boolean {
    return this.signature?.text || this.signature?.file;
  }

  private _signaturePad: SignaturePad;

  private _subscription = new Subscription();

  constructor() {
    this._subscription.add(
      this.isModalVisible$
        .pipe(
          delay(300),
          filter((value: boolean) => value),
        )
        .subscribe(() => {
          const canvas = document.getElementById('signature-pad') as HTMLCanvasElement;

          this._signaturePad = new SignaturePad(canvas);
          this.resizeCanvas();

          if (this.signature.file) {
            this._signaturePad.fromDataURL(this.signature.file);
          }
        })
    );
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public clear(): void {
    this._signaturePad?.clear();
  }

  public save(): void {
    let result = { file: null, text: null };

    switch (this.activeTab$.value) {
      case SIGNATURE_TAB.draw:
        result = {
          ...result,
          file: this._signaturePad.toDataURL()
        };
        break;
      case SIGNATURE_TAB.type:
        result = {
          ...result,
          text: this.signature.text
        };
        break;
    }

    this.changeSignEvent.next(result);
    this.isModalVisible$.next(false);
    this.activeTab$.next(SIGNATURE_TAB.draw);
  }

  public onTabChange({ index }): void {
    this.activeTab$.next(index);
  }

  private resizeCanvas(): void {
    const canvas = document.getElementById('signature-pad') as HTMLCanvasElement;
    // When zoomed out to less than 100%, for some very strange reason,
    // some browsers report devicePixelRatio as less than 1
    // and only part of the canvas is cleared then.
    const ratio =  Math.max(window.devicePixelRatio || 1, 1);

    // This part causes the canvas to be cleared
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);

    // This library does not listen for canvas changes, so after the canvas is automatically
    // cleared by the browser, SignaturePad#isEmpty might still return false, even though the
    // canvas looks empty, because the internal data of this library wasn't cleared. To make sure
    // that the state of this library is consistent with visual state of the canvas, you
    // have to clear it manually.
    this._signaturePad.clear();
  }
}
